import React, { useRef, useState } from "react";

const statusLabels = {
  draft: "검토 대기",
  approved: "승인",
  rejected: "거절",
  ready_to_apply: "적용 준비",
  applied: "적용됨",
  verified: "검증 완료",
  verify_failed: "검증 실패",
  apply_failed: "적용 실패",
  restored: "복구 완료",
  restore_failed: "복구 실패",
  dry_run_passed: "사전검증 통과",
  dry_run_failed: "사전검증 실패",
};

function ProposalSummary({ text }) {
  const blocks = String(text || "")
    .replace(/\s+([1-9]\.\s)/g, "\n\n$1")
    .replace(/\n{3,}/g, "\n\n")
    .trim()
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean);

  if (blocks.length === 0) return null;

  return (
    <div className="proposal-summary">
      {blocks.map((block, index) => {
        const lines = block.split("\n").map((line) => line.trim()).filter(Boolean);
        const firstLine = lines[0] || "";
        const isNumberedTitle = /^\d+\.\s/.test(firstLine);
        return (
          <div key={`${firstLine}-${index}`} className="proposal-summary-block">
            {isNumberedTitle ? <strong>{firstLine}</strong> : <p>{firstLine}</p>}
            {lines.slice(1).map((line, lineIndex) => (
              <p key={`${line}-${lineIndex}`}>{line}</p>
            ))}
          </div>
        );
      })}
    </div>
  );
}

function TargetFiles({ files = [] }) {
  if (!files.length) return null;
  const visible = files.slice(0, 4);
  const hidden = files.slice(4);

  return (
    <div className="proposal-targets">
      <span>대상 파일</span>
      <div className="proposal-target-chip-row">
        {visible.map((file) => (
          <code key={file}>{file}</code>
        ))}
      </div>
      {hidden.length > 0 && (
        <details>
          <summary>추가 대상 {hidden.length}개 보기</summary>
          <div className="proposal-target-chip-row">
            {hidden.map((file) => (
              <code key={file}>{file}</code>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function CodexEngineReport({ report = {}, verificationPlan = {} }) {
  const components = report.components || [];
  const resolvedFiles = report.resolved_files || [];
  const missingFiles = report.missing_files || [];
  const commands = verificationPlan.commands || report.verification_plan?.commands || [];
  const gates = verificationPlan.safety_gates || report.verification_plan?.safety_gates || [];

  if (!report.engine_version && commands.length === 0) return null;

  return (
    <details className="codex-engine-report" open>
      <summary>Codex 기술엔진 분석</summary>
      <div className="codex-engine-grid">
        <div>
          <span>분류</span>
          <strong>{components.length ? components.join(", ") : report.request_type || "-"}</strong>
        </div>
        <div>
          <span>계산 위험도</span>
          <strong className={`risk-${report.computed_risk || "medium"}`}>{report.computed_risk || "medium"}</strong>
        </div>
        <div>
          <span>대상 파일</span>
          <strong>{resolvedFiles.filter((item) => item.exists).length}개 확인 / {missingFiles.length}개 누락</strong>
        </div>
      </div>
      {report.engine_summary && <p>{report.engine_summary}</p>}
      {resolvedFiles.length > 0 && (
        <div className="proposal-targets">
          <span>파일 확인 결과</span>
          <div className="proposal-target-chip-row">
            {resolvedFiles.slice(0, 8).map((item) => (
              <code key={`${item.requested}-${item.path}`} className={item.exists ? "exists" : "missing"}>
                {item.exists ? "OK " : "MISS "}{item.requested}
              </code>
            ))}
          </div>
        </div>
      )}
      {commands.length > 0 && (
        <div className="codex-engine-list">
          <span>검증 명령</span>
          <ol>
            {commands.slice(0, 8).map((command) => <li key={command}>{command}</li>)}
          </ol>
        </div>
      )}
      {gates.length > 0 && (
        <div className="codex-engine-list">
          <span>안전 게이트</span>
          <ul>
            {gates.slice(0, 8).map((gate) => <li key={gate}>{gate}</li>)}
          </ul>
        </div>
      )}
    </details>
  );
}

function ProposalItem({
  item,
  onDecideProposal,
  onCreatePatch,
  onDeleteProposal,
  patchingId,
  existingPatch,
  readonly = false,
  compact = false,
}) {
  const statusText = `${statusLabels[item.status] || item.status} · 위험도 ${item.risk_level || "medium"}`;

  const body = (
    <>
      <ProposalSummary text={item.summary} />
      <TargetFiles files={item.target_files || []} />
      <CodexEngineReport report={item.engine_report || {}} verificationPlan={item.verification_plan || {}} />
      {onDeleteProposal && (
        <span className="agent-action-row">
          <button className="admin-danger-button" onClick={() => onDeleteProposal(item.id)}>삭제</button>
        </span>
      )}
      {!readonly && item.status === "draft" && (
        <span className="agent-action-row">
          <button className="admin-approve-button" onClick={() => onDecideProposal(item.id, true)}>승인</button>
          <button className="admin-hold-button" onClick={() => onDecideProposal(item.id, false)}>거절</button>
        </span>
      )}
      {!readonly && item.status === "approved" && (
        <span className="agent-action-row">
          <button className="admin-approve-button" disabled={patchingId === item.id || !!existingPatch} onClick={() => onCreatePatch(item.id)}>
            {existingPatch ? "패치 초안 생성됨" : patchingId === item.id ? "패치 생성 중..." : "패치 초안 생성"}
          </button>
          {existingPatch && <small>아래 패치 초안 목록에서 확인하세요.</small>}
        </span>
      )}
    </>
  );

  if (compact) {
    return (
      <li className={`code-proposal-item status-${item.status} compact-proposal-item`}>
        <details>
          <summary>
            <strong>{item.title}</strong>
            <small>{statusText}</small>
          </summary>
          {body}
        </details>
      </li>
    );
  }

  return (
    <li className={`code-proposal-item status-${item.status}`}>
      <div className="proposal-item-head">
        <div>
          <strong>{item.title}</strong>
          <small>{statusText}</small>
        </div>
      </div>
      {body}
    </li>
  );
}

function BlueprintPanel({ blueprint, onCreateProposal, proposing }) {
  if (!blueprint) return null;
  const chips = [
    ...(blueprint.core_screens || []).map((item) => `화면: ${item}`),
    ...(blueprint.data_models || []).map((item) => `데이터: ${item}`),
    ...(blueprint.backend_apis || []).map((item) => `API: ${item}`),
  ];

  return (
    <div className="codex-app-blueprint">
      <div className="deployment-history-header">
        <div>
          <strong>앱 제작 블루프린트</strong>
          <span>{blueprint.summary}</span>
        </div>
        <button className="admin-approve-button" disabled={proposing} onClick={() => onCreateProposal?.(null, blueprint.starter_instruction || "")}>
          {proposing ? "제안서 생성 중..." : "이 설계로 코드 제안 생성"}
        </button>
      </div>
      <div className="codex-blueprint-grid">
        <div><span>앱 유형</span><strong>{blueprint.app_kind}</strong></div>
        <div><span>위험도</span><strong>{blueprint.risk_level}</strong></div>
        <div><span>대상 파일</span><strong>{(blueprint.target_files || []).length}개</strong></div>
      </div>
      <div className="codex-project-area-row">
        {chips.slice(0, 14).map((item) => <span key={item}>{item}</span>)}
      </div>
      <ProposalSummary text={(blueprint.implementation_steps || []).map((step, index) => `${index + 1}. ${step}`).join("\n\n")} />
      <details className="codex-blueprint-details">
        <summary>검증 기준과 추가 질문 보기</summary>
        <div className="codex-blueprint-columns">
          <div>
            <strong>검증 기준</strong>
            <ul>{(blueprint.acceptance_tests || []).map((item) => <li key={item}>{item}</li>)}</ul>
          </div>
          <div>
            <strong>추가 질문</strong>
            <ul>{(blueprint.questions || []).map((item) => <li key={item}>{item}</li>)}</ul>
          </div>
        </div>
      </details>
      <TargetFiles files={blueprint.target_files || []} />
    </div>
  );
}

function SourceMapPanel({ sourceMap }) {
  if (!sourceMap) return null;
  const statItems = [
    ["파일", `${sourceMap.file_count || 0}개`],
    ["압축 해제", `${sourceMap.extracted_files || 0}개`],
    ["예상 루트", sourceMap.likely_root || "-"],
    ["스택", (sourceMap.detected_stack || []).join(", ") || "-"],
  ];

  return (
    <div className="codex-source-map">
      <div className="deployment-history-header">
        <div>
          <strong>기존 소스 ZIP 분석</strong>
          <span>{sourceMap.summary}</span>
        </div>
      </div>
      <div className="codex-blueprint-grid">
        {statItems.map(([label, value]) => (
          <div key={label}><span>{label}</span><strong>{value}</strong></div>
        ))}
      </div>
      <div className="codex-source-path">
        <span>해제 위치</span>
        <code>{sourceMap.extract_path}</code>
      </div>
      <div className="codex-blueprint-columns">
        <div>
          <strong>시작 파일</strong>
          <ul>{(sourceMap.entrypoints || []).slice(0, 12).map((item) => <li key={item}>{item}</li>)}</ul>
        </div>
        <div>
          <strong>핵심 파일</strong>
          <ul>{(sourceMap.key_files || []).slice(0, 12).map((item) => <li key={item}>{item}</li>)}</ul>
        </div>
      </div>
      <details className="codex-blueprint-details" open={Boolean(sourceMap.sensitive_files?.length)}>
        <summary>위험/자가 점검 항목 보기</summary>
        <div className="codex-blueprint-columns">
          <div>
            <strong>민감 파일 후보</strong>
            <ul>{(sourceMap.sensitive_files || ["없음"]).slice(0, 12).map((item) => <li key={item}>{item}</li>)}</ul>
          </div>
          <div>
            <strong>분석 권장 순서</strong>
            <ul>{(sourceMap.recommendations || []).map((item) => <li key={item}>{item}</li>)}</ul>
          </div>
        </div>
      </details>
      <div className="codex-project-file-list">
        {(sourceMap.sample_files || []).slice(0, 24).map((file) => <code key={file}>{file}</code>)}
      </div>
    </div>
  );
}

function ImageBriefPanel({ imageBrief, onCreateProposal, proposing }) {
  if (!imageBrief) return null;
  const localVision = imageBrief.local_vision || {};
  const ocr = imageBrief.ocr || {};
  const layout = imageBrief.layout || {};

  return (
    <div className="codex-image-brief">
      <div className="deployment-history-header">
        <div>
          <strong>참조 이미지 로컬 비전 분석</strong>
          <span>{imageBrief.summary}</span>
        </div>
        <button className="admin-approve-button" disabled={proposing} onClick={() => onCreateProposal?.(null, imageBrief.starter_instruction || "")}>
          {proposing ? "제안서 생성 중..." : "이 이미지 기준으로 UI 제안 생성"}
        </button>
      </div>
      <div className="codex-blueprint-grid">
        <div><span>포맷</span><strong>{imageBrief.format}</strong></div>
        <div><span>크기</span><strong>{imageBrief.width} x {imageBrief.height}</strong></div>
        <div><span>비전 방식</span><strong>{imageBrief.vision_provider}</strong></div>
        <div><span>방향</span><strong>{imageBrief.orientation}</strong></div>
        <div><span>톤/대비</span><strong>{layout.available ? `${layout.tone} / ${layout.density}` : "fallback"}</strong></div>
        <div><span>로컬 모델</span><strong>{localVision.available ? localVision.model : "미설정"}</strong></div>
      </div>
      <div className="codex-palette-row">
        {(imageBrief.palette || []).length === 0 ? (
          <span>색상 팔레트는 Pillow가 있을 때 더 정밀하게 추출됩니다.</span>
        ) : (
          (imageBrief.palette || []).map((color) => (
            <span key={color.hex} style={{ background: color.hex }} title={color.hex}>{color.hex}</span>
          ))
        )}
      </div>
      <div className="proposal-summary">
        {localVision.available ? (
          <div className="proposal-summary-block">
            <strong>로컬 Ollama 비전 분석</strong>
            <p>{localVision.description}</p>
          </div>
        ) : (
          <div className="proposal-summary-block">
            <strong>로컬 비전 모델 상태</strong>
            <p>{localVision.reason || "OLLAMA_VISION_MODEL이 설정되지 않았습니다."}</p>
          </div>
        )}
        {ocr.text && (
          <div className="proposal-summary-block">
            <strong>OCR 텍스트</strong>
            <p>{ocr.text}</p>
          </div>
        )}
        <div className="proposal-summary-block">
          <strong>이미지 생성 프롬프트</strong>
          <p>{imageBrief.generation_prompt}</p>
        </div>
        <div className="proposal-summary-block">
          <strong>UI 재현 지시문</strong>
          <p>{imageBrief.ui_reproduction_prompt}</p>
        </div>
      </div>
      <details className="codex-blueprint-details">
        <summary>무료 로컬 분석 기준 보기</summary>
        <div className="codex-blueprint-columns">
          <div>
            <strong>재현 기준</strong>
            <ul>{(imageBrief.visual_requirements || []).map((item) => <li key={item}>{item}</li>)}</ul>
          </div>
          <div>
            <strong>분석 방식</strong>
            <ul>{(imageBrief.analysis_limits || []).map((item) => <li key={item}>{item}</li>)}</ul>
          </div>
        </div>
      </details>
    </div>
  );
}

function PlanningBriefPanel({ planningBrief, onCreateProposal, proposing }) {
  if (!planningBrief) return null;
  const chips = [
    ...(planningBrief.screens || []).map((item) => `화면: ${item}`),
    ...(planningBrief.data_models || []).map((item) => `데이터: ${item}`),
    ...(planningBrief.backend_apis || []).map((item) => `API: ${item}`),
  ];

  return (
    <div className="codex-planning-brief">
      <div className="deployment-history-header">
        <div>
          <strong>기획서 분석 및 구현 계획</strong>
          <span>{planningBrief.summary}</span>
        </div>
        <button className="admin-approve-button" disabled={proposing} onClick={() => onCreateProposal?.(null, planningBrief.starter_instruction || "")}>
          {proposing ? "제안서 생성 중..." : "이 계획으로 코드 제안 생성"}
        </button>
      </div>
      <div className="codex-blueprint-grid">
        <div><span>앱 유형</span><strong>{planningBrief.app_kind}</strong></div>
        <div><span>위험도</span><strong>{planningBrief.risk_level}</strong></div>
        <div><span>문서 길이</span><strong>{planningBrief.document_chars || 0}자</strong></div>
      </div>
      <div className="codex-project-area-row">
        {chips.slice(0, 16).map((item) => <span key={item}>{item}</span>)}
      </div>
      <div className="proposal-summary">
        <div className="proposal-summary-block">
          <strong>핵심 요구사항</strong>
          {(planningBrief.requirements || []).slice(0, 8).map((item) => <p key={item}>{item}</p>)}
        </div>
        {(planningBrief.implementation_steps || []).map((step, index) => (
          <div className="proposal-summary-block" key={`${step}-${index}`}>
            <strong>{index + 1}. {step}</strong>
          </div>
        ))}
      </div>
      <details className="codex-blueprint-details" open={Boolean(planningBrief.risk_notes?.length)}>
        <summary>검증 기준과 위험 항목 보기</summary>
        <div className="codex-blueprint-columns">
          <div>
            <strong>검증 기준</strong>
            <ul>{(planningBrief.acceptance_tests || []).map((item) => <li key={item}>{item}</li>)}</ul>
          </div>
          <div>
            <strong>위험 항목</strong>
            <ul>{(planningBrief.risk_notes || ["명확한 위험 항목이 감지되지 않았습니다."]).map((item) => <li key={item}>{item}</li>)}</ul>
          </div>
        </div>
      </details>
      <TargetFiles files={planningBrief.target_files || []} />
    </div>
  );
}

function DocumentTaskPanel({
  onRun,
  onCreateAppBlueprint,
  onAnalyzeSourceZip,
  onAnalyzeReferenceImage,
  onAnalyzePlanningDocument,
  onCreateTaskPlan,
  onCreateProposal,
  onDecideProposal,
  onDeleteProposal,
  onCreatePatch,
  onDeletePatch,
  onRegeneratePatch,
  onDryRunPatch,
  onPreparePatch,
  onApplyPatch,
  onVerifyPatch,
  onRestorePatch,
  running,
  blueprinting,
  analyzingSourceZip,
  analyzingReferenceImage,
  analyzingPlanningDocument,
  proposing,
  patchingId,
  dryRunningPatchId,
  preparingPatchId,
  applyingPatchId,
  verifyingPatchId,
  restoringPatchId,
  proposals = [],
  patches = [],
  projectMap = null,
  appBlueprint = null,
  sourceMap = null,
  imageBrief = null,
  planningBrief = null,
  taskPlan = null,
}) {
  const fileRef = useRef(null);
  const sourceZipRef = useRef(null);
  const referenceImageRef = useRef(null);
  const planningDocRef = useRef(null);
  const [fileName, setFileName] = useState("");
  const [sourceZipName, setSourceZipName] = useState("");
  const [referenceImageName, setReferenceImageName] = useState("");
  const [planningDocName, setPlanningDocName] = useState("");
  const [instruction, setInstruction] = useState("");
  const appPrompts = [
    "회사 홈페이지를 만들고 관리자에서 문구와 문의를 관리하게 해줘.",
    "상품 목록, 장바구니, 주문 관리가 있는 쇼핑몰을 만들어줘.",
    "예약 신청과 관리자 일정 관리가 있는 예약 앱을 만들어줘.",
    "AI 상담 챗봇과 지식 업로드 화면을 만들어줘.",
  ];
  const activeProposals = proposals.filter((item) => item.status !== "rejected");
  const rejectedProposals = proposals.filter((item) => item.status === "rejected");
  const patchByProposalId = new Map(
    patches
      .filter((item) => ["draft", "ready_to_apply"].includes(item.status))
      .map((item) => [Number(item.proposal_id), item])
  );

  const handleFileChange = (event) => setFileName(event.target.files?.[0]?.name || "");
  const handleSourceZipChange = (event) => setSourceZipName(event.target.files?.[0]?.name || "");
  const handleReferenceImageChange = (event) => setReferenceImageName(event.target.files?.[0]?.name || "");
  const handlePlanningDocChange = (event) => setPlanningDocName(event.target.files?.[0]?.name || "");
  const handleRun = () => onRun(fileRef.current?.files?.[0] || null, instruction);
  const handleProposal = () => onCreateProposal(fileRef.current?.files?.[0] || null, instruction);

  return (
    <section className="document-task-panel">
      <div>
        <p className="panel-kicker">TOBE CODEX</p>
        <h3>작업 지시 기반 코드 실행실</h3>
        <p className="dashboard-muted">
          작업 문서나 직접 지시를 넣으면 관련 파일 탐색, 작업 계획, 코드 수정 제안, 패치 생성, 검증, 백업, 적용, 복구까지 한 화면에서 처리합니다.
        </p>
      </div>

      <div className="document-task-controls">
        <div className="codex-source-analyzer">
          <div>
            <p className="panel-kicker">기존 소스 분석</p>
            <h4>대용량 ZIP을 읽고 출발점을 찾습니다</h4>
            <p>압축 내부 구조, 기술스택, 시작 파일, 민감 파일 후보, 분석 순서를 먼저 파악합니다.</p>
          </div>
          <label className="document-file-picker">
            <input ref={sourceZipRef} type="file" accept=".zip" onChange={handleSourceZipChange} />
            <span>{sourceZipName || "기존 소스 ZIP 선택"}</span>
          </label>
          <button className="admin-approve-button document-run-button" disabled={analyzingSourceZip} onClick={() => onAnalyzeSourceZip?.(sourceZipRef.current?.files?.[0] || null)}>
            {analyzingSourceZip ? "ZIP 분석 중..." : "ZIP 읽기 및 구조 분석"}
          </button>
        </div>

        <div className="codex-image-analyzer">
          <div>
            <p className="panel-kicker">이미지 제작 기술</p>
            <h4>참조 이미지를 읽고 재현 기준을 만듭니다</h4>
            <p>이미지 포맷, 크기, 비율, 색상, 레이아웃을 분석하고 UI 제작 제안으로 연결합니다.</p>
          </div>
          <label className="document-file-picker">
            <input ref={referenceImageRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" onChange={handleReferenceImageChange} />
            <span>{referenceImageName || "참조 이미지 선택"}</span>
          </label>
          <button className="admin-approve-button document-run-button" disabled={analyzingReferenceImage} onClick={() => onAnalyzeReferenceImage?.(referenceImageRef.current?.files?.[0] || null, instruction)}>
            {analyzingReferenceImage ? "이미지 분석 중..." : "이미지 분석 및 제작 프롬프트 생성"}
          </button>
        </div>

        <div className="codex-planning-analyzer">
          <div>
            <p className="panel-kicker">기획서 구현</p>
            <h4>기획서를 분석하고 구현 계획을 만듭니다</h4>
            <p>요구사항, 화면, API, 데이터, 권한, 위험도, 검증 기준을 추출하고 코드 제안으로 연결합니다.</p>
          </div>
          <label className="document-file-picker">
            <input ref={planningDocRef} type="file" accept=".md,.txt,.json,.csv,.docx" onChange={handlePlanningDocChange} />
            <span>{planningDocName || "기획서 파일 선택"}</span>
          </label>
          <button className="admin-approve-button document-run-button" disabled={analyzingPlanningDocument} onClick={() => onAnalyzePlanningDocument?.(planningDocRef.current?.files?.[0] || null, instruction)}>
            {analyzingPlanningDocument ? "기획서 분석 중..." : "기획서 분석 및 작업계획 생성"}
          </button>
        </div>

        <div className="codex-conversation-builder">
          <div>
            <p className="panel-kicker">대화형 제작</p>
            <h4>무슨 앱이든 홈페이지든 말로 시작하세요</h4>
            <p>TOBE CODEX가 화면, 데이터, API, 검증 기준을 먼저 설계하고 코드 제안으로 넘깁니다.</p>
          </div>
          <div className="codex-prompt-row">
            {appPrompts.map((prompt) => (
              <button key={prompt} type="button" className="admin-hold-button" onClick={() => setInstruction(prompt)}>
                {prompt}
              </button>
            ))}
          </div>
          <button className="admin-approve-button document-run-button" disabled={blueprinting} onClick={() => onCreateAppBlueprint?.(instruction)}>
            {blueprinting ? "블루프린트 생성 중..." : "앱 제작 블루프린트 생성"}
          </button>
        </div>

        <label className="document-file-picker">
          <input ref={fileRef} type="file" accept=".md,.txt,.json,.csv,.py,.js,.jsx,.sql" onChange={handleFileChange} />
          <span>{fileName || "작업 문서 선택"}</span>
        </label>
        <textarea
          value={instruction}
          onChange={(event) => setInstruction(event.target.value)}
          placeholder="예: 관리자 배포 화면에 실패 로그 요약과 재시작 버튼을 추가하고, 백엔드 검증 API까지 연결해줘."
          rows={3}
        />
        <div className="document-task-actions">
          <button className="admin-hold-button document-run-button" disabled={proposing} onClick={() => onCreateTaskPlan?.(instruction)}>
            Codex 계획
          </button>
          <button className="admin-hold-button document-run-button" disabled={proposing} onClick={handleProposal}>
            {proposing ? "제안서 생성 중..." : "코드 수정 제안서 생성"}
          </button>
          <button className="admin-approve-button document-run-button" disabled={running} onClick={handleRun}>
            {running ? "분석 요청 중..." : "분석 및 실행"}
          </button>
        </div>
      </div>

      <SourceMapPanel sourceMap={sourceMap} />
      <ImageBriefPanel imageBrief={imageBrief} onCreateProposal={onCreateProposal} proposing={proposing} />
      <PlanningBriefPanel planningBrief={planningBrief} onCreateProposal={onCreateProposal} proposing={proposing} />
      <BlueprintPanel blueprint={appBlueprint} onCreateProposal={onCreateProposal} proposing={proposing} />

      {taskPlan && (
        <div className="codex-task-plan">
          <div className="deployment-history-header">
            <strong>Codex 작업 계획</strong>
            <span>{taskPlan.summary}</span>
          </div>
          <div className="codex-project-area-row">
            {(taskPlan.areas || []).map((area) => <span key={area}>{area}</span>)}
            <span>risk: {taskPlan.risk_level}</span>
          </div>
          <ProposalSummary text={(taskPlan.execution_steps || []).slice(0, 8).map((step, index) => `${index + 1}. ${step}`).join("\n\n")} />
          <TargetFiles files={taskPlan.target_files || []} />
        </div>
      )}

      {projectMap && (
        <div className="codex-project-map">
          <div className="deployment-history-header">
            <strong>Codex 프로젝트 맵</strong>
            <span>{projectMap.summary}</span>
          </div>
          <div className="codex-project-area-row">
            {Object.entries(projectMap.areas || {}).slice(0, 8).map(([area, info]) => (
              <span key={area}>{area}: {info.file_count}개</span>
            ))}
          </div>
          <div className="codex-project-file-list">
            {(projectMap.files || []).slice(0, 10).map((file) => (
              <code key={file.absolute_path || file.path}>{file.area} / {file.path}</code>
            ))}
          </div>
        </div>
      )}

      <div className="code-proposal-list">
        <h4>코드 수정 제안서</h4>
        {activeProposals.length === 0 ? (
          <p className="dashboard-muted">아직 생성된 제안서가 없습니다.</p>
        ) : (
          <ul>
            {activeProposals.map((item) => (
              <ProposalItem
                key={item.id}
                item={item}
                onDecideProposal={onDecideProposal}
                onCreatePatch={onCreatePatch}
                onDeleteProposal={onDeleteProposal}
                patchingId={patchingId}
                existingPatch={patchByProposalId.get(Number(item.id))}
              />
            ))}
          </ul>
        )}
        {rejectedProposals.length > 0 && (
          <details className="archived-proposal-box">
            <summary>최근 거절된 제안 {rejectedProposals.length}개 보기</summary>
            <ul>
              {rejectedProposals.map((item) => (
                <ProposalItem key={item.id} item={item} onDeleteProposal={onDeleteProposal} readonly compact />
              ))}
            </ul>
          </details>
        )}
      </div>

      <div className="code-proposal-list">
        <h4>패치 초안</h4>
        {patches.length === 0 ? (
          <p className="dashboard-muted">아직 패치 초안이 없습니다.</p>
        ) : (
          <ul>
            {patches.map((item) => (
              <li key={item.id} className={`code-proposal-item status-${item.status}`}>
                <div>
                  <strong>{item.title}</strong>
                  <small>{statusLabels[item.status] || item.status} · 제안 #{item.proposal_id}</small>
                </div>
                <div className={`patch-kind-badge ${item.is_unified_diff ? "diff" : "plan"}`}>
                  {item.is_unified_diff ? "실제 적용 가능 패치" : "검토 계획안"}
                </div>
                {item.patch_files?.length > 0 && <TargetFiles files={item.patch_files} />}
                <pre className="patch-preview">{item.patch_text}</pre>
                {!item.is_unified_diff && (
                  <p className="patch-safety-note">
                    이 초안은 검토 계획서입니다. 실제 파일에 바로 적용할 수 있는 unified diff 패치가 아니므로 최종 적용 버튼은 숨겨집니다.
                  </p>
                )}
                {item.apply_note && <p>{item.apply_note}</p>}
                <span className="agent-action-row">
                  {onRegeneratePatch && !["applied", "verified", "verify_failed"].includes(item.status) && (
                    <button className="admin-hold-button" onClick={() => onRegeneratePatch(item.id)}>재생성</button>
                  )}
                  {item.status === "draft" && item.is_unified_diff && onDryRunPatch && (
                    <button className="admin-hold-button" disabled={dryRunningPatchId === item.id} onClick={() => onDryRunPatch(item.id)}>
                      {dryRunningPatchId === item.id ? "사전검증 중..." : "사전검증"}
                    </button>
                  )}
                  {item.status === "draft" && item.is_unified_diff && (
                    <button className="admin-approve-button" disabled={preparingPatchId === item.id} onClick={() => onPreparePatch(item.id)}>
                      {preparingPatchId === item.id ? "백업 준비 중..." : "백업 준비"}
                    </button>
                  )}
                  {item.patch_url && <a className="admin-link-button" href={item.patch_url} target="_blank" rel="noreferrer">패치 파일</a>}
                  {item.backup_url && <a className="admin-link-button" href={item.backup_url} target="_blank" rel="noreferrer">백업 ZIP</a>}
                  {item.status === "ready_to_apply" && item.is_unified_diff && (
                    <button className="admin-danger-button" disabled={applyingPatchId === item.id} onClick={() => onApplyPatch(item.id)}>
                      {applyingPatchId === item.id ? "적용 중..." : "최종 적용"}
                    </button>
                  )}
                  {["applied", "verified", "verify_failed"].includes(item.status) && (
                    <button className="admin-approve-button" disabled={verifyingPatchId === item.id} onClick={() => onVerifyPatch(item.id)}>
                      {verifyingPatchId === item.id ? "검증 중..." : "검증 실행"}
                    </button>
                  )}
                  {item.backup_url && ["applied", "verified", "verify_failed", "apply_failed"].includes(item.status) && (
                    <button className="admin-danger-button" disabled={restoringPatchId === item.id} onClick={() => onRestorePatch(item.id)}>
                      {restoringPatchId === item.id ? "복구 중..." : "백업 복구"}
                    </button>
                  )}
                  {onDeletePatch && (
                    <button className="admin-danger-button" onClick={() => onDeletePatch(item.id)}>삭제</button>
                  )}
                </span>
                {item.validation_status && <small>검증 상태: {statusLabels[item.validation_status] || item.validation_status}</small>}
                {item.validation_log && <pre className="patch-preview validation-log">{item.validation_log}</pre>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

export default DocumentTaskPanel;
